import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { ApiResponse } from '../models/api-response.models';
import {
  CreateProductRequest,
  CategoryProfitRule,
  CategoryProfitRuleMutation,
  Product,
  ProductBrand,
  ProductBrandMutation,
  ProductCategory,
  ProductCategoryMutation,
  ProductCategoryOption,
  ProductQuery,
} from '../models/product.models';
import { PagedResponse } from '../models/user.models';
import { unwrapApiResponse } from '../utils/api-response';
import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';

export type ReviewQueueStatus = 'pending' | 'reported' | 'active' | 'rejected';
export interface ReviewQueueResult {
  products: Product[];
  counts: Record<ReviewQueueStatus, number>;
  hasCompleteCounts: boolean;
}
export interface ProductPage {
  products: Product[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private readonly api = inject(ApiService);

  list(query: ProductQuery = {}): Observable<Product[]> {
    return this.listPage(query).pipe(map((page) => page.products));
  }

  listPage(query: ProductQuery = {}): Observable<ProductPage> {
    return this.api
      .get<ApiResponse<Product[] | PagedResponse<Product>> | Product[] | PagedResponse<Product>>(
        API_ENDPOINTS.products.base,
        {
          search: query.search ?? '',
          condition: query.listingType,
          mainCategory: query.mainCategory,
          itemType: query.itemType ?? query.subCategory,
          categoryId: query.categoryId,
          pageNumber: query.pageNumber ?? 1,
          pageSize: query.pageSize ?? 10,
        },
        'marketplace',
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => {
          const products = this.extractProducts(response);
          if (Array.isArray(response)) {
            return {
              products,
              totalCount: products.length,
              pageNumber: query.pageNumber ?? 1,
              pageSize: query.pageSize ?? 10,
            };
          }
          const page = response as PagedResponse<Product> & { products?: Product[] };
          return {
            products,
            totalCount: Number(page.totalCount ?? products.length),
            pageNumber: Number(page.pageNumber ?? query.pageNumber ?? 1),
            pageSize: Number(page.pageSize ?? query.pageSize ?? 10),
          };
        }),
      );
  }

  listBySeller(
    _sellerId?: string,
    _listingType?: ProductQuery['listingType'],
  ): Observable<Product[]> {
    return this.api
      .get<ApiResponse<Product[] | PagedResponse<Product>> | Product[] | PagedResponse<Product>>(
        API_ENDPOINTS.products.me,
        undefined,
        'marketplace',
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => this.extractProducts(response)),
      );
  }

  reviewQueue(status: ReviewQueueStatus | 'all' = 'pending'): Observable<ReviewQueueResult> {
    return this.api
      .get<ApiResponse<ReviewQueueResult> | ReviewQueueResult>(
        API_ENDPOINTS.products.reviewQueue,
        { status },
        'marketplace',
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => {
          const products = response.products ?? [];
          const hasCompleteCounts = Boolean(response.counts);
          const counts = response.counts
            ? { ...response.counts }
            : { pending: 0, reported: 0, active: 0, rejected: 0 };
          if (!hasCompleteCounts && status !== 'all') counts[status] = products.length;
          return { products, counts, hasCompleteCounts };
        }),
      );
  }

  reviewDetail(productId: string): Observable<Product> {
    return this.api
      .get<
        | ApiResponse<{
            product: Product;
            reports?: Product['reports'];
            auditTrail?: Product['auditTrail'];
          }>
        | { product: Product; reports?: Product['reports']; auditTrail?: Product['auditTrail'] }
      >(API_ENDPOINTS.products.reviewDetail(productId), undefined, 'marketplace')
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => ({
          ...response.product,
          reports: response.reports ?? [],
          auditTrail: response.auditTrail ?? [],
        })),
      );
  }

  reviewCounts(): Observable<Record<ReviewQueueStatus, number>> {
    return this.api
      .get<ApiResponse<Record<ReviewQueueStatus, number>> | Record<ReviewQueueStatus, number>>(
        API_ENDPOINTS.products.reviewCounts,
        undefined,
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  moderate(
    productId: string,
    action: 'Approve' | 'Hold' | 'Reject' | 'Deactivate',
    reason?: string,
  ): Observable<void> {
    return this.api
      .patch<ApiResponse<unknown> | unknown, { action: string; reason?: string }>(
        API_ENDPOINTS.products.moderate(productId),
        { action, reason },
        'marketplace',
      )
      .pipe(map(() => undefined));
  }

  appealModeration(productId: string, reason: string): Observable<void> {
    return this.api
      .post<ApiResponse<unknown> | unknown, { reason: string }>(
        `/products/${productId}/moderation-appeals`,
        { reason },
        'marketplace',
      )
      .pipe(map(() => undefined));
  }

  updateAdmin(
    productId: string,
    payload: {
      title: string;
      description?: string;
      modelName?: string;
      cost?: number;
      repairCost?: number;
      extraCost?: number;
      sellingPrice: number;
      isReadyToSell: boolean;
      images?: File[];
    },
  ): Observable<Product> {
    const formData = new FormData();
    this.append(formData, 'title', payload.title);
    this.append(formData, 'description', payload.description);
    this.append(formData, 'modelName', payload.modelName);
    this.append(formData, 'cost', payload.cost);
    this.append(formData, 'repairCost', payload.repairCost);
    this.append(formData, 'extraCost', payload.extraCost);
    this.append(formData, 'sellingPrice', payload.sellingPrice);
    this.append(formData, 'isReadyToSell', String(payload.isReadyToSell));
    for (const image of payload.images ?? []) formData.append('images', image, image.name);
    return this.api
      .put<ApiResponse<Product | { product: Product }> | Product | { product: Product }, FormData>(
        `/admin/products/${productId}`,
        formData,
        'marketplace',
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => ('product' in response ? response.product : response)),
      );
  }

  listOldByCustomer(_customerId?: string): Observable<Product[]> {
    return this.api
      .get<ApiResponse<Product[] | PagedResponse<Product>> | Product[] | PagedResponse<Product>>(
        API_ENDPOINTS.products.myUsed,
        undefined,
        'marketplace',
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => this.extractProducts(response)),
      );
  }

  create(payload: CreateProductRequest): Observable<Product> {
    return this.api
      .post<ApiResponse<Product | { product: Product }> | Product | { product: Product }, FormData>(
        API_ENDPOINTS.products.base,
        this.toFormData(payload),
        'marketplace',
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) =>
          typeof response === 'object' && response !== null && 'product' in response
            ? (response as { product: Product }).product
            : response,
        ),
      );
  }

  categories(): Observable<Array<ProductCategoryOption | ProductCategory>> {
    return this.api
      .get<
        | ApiResponse<ProductCategoryOption[] | ProductCategory[]>
        | ProductCategoryOption[]
        | ProductCategory[]
      >(API_ENDPOINTS.products.categories, undefined, 'marketplace')
      .pipe(map((response) => this.extractCollection(unwrapApiResponse(response), 'categories')));
  }

  childCategories(parentCategoryId: string): Observable<ProductCategory[]> {
    return this.api
      .get<ApiResponse<ProductCategory[]> | ProductCategory[]>(
        API_ENDPOINTS.products.categoryChildren(parentCategoryId),
        undefined,
        'marketplace',
      )
      .pipe(map((response) => this.extractCollection(unwrapApiResponse(response), 'categories')));
  }

  brands(categoryId?: string): Observable<ProductBrand[]> {
    return this.api
      .get<ApiResponse<ProductBrand[]> | ProductBrand[]>(
        API_ENDPOINTS.products.brands,
        categoryId ? { categoryId } : undefined,
        'marketplace',
      )
      .pipe(map((response) => this.extractCollection(unwrapApiResponse(response), 'brands')));
  }

  createCategory(payload: ProductCategoryMutation): Observable<ProductCategory> {
    return this.api
      .post<
        | ApiResponse<ProductCategory | { category: ProductCategory }>
        | ProductCategory
        | { category: ProductCategory },
        FormData
      >(
        API_ENDPOINTS.products.adminCategories,
        this.catalogFormData(payload),
        'marketplace',
        environment.catalogUploadTimeoutMs,
      )
      .pipe(map((response) => this.extractCatalogItem(unwrapApiResponse(response), 'category')));
  }

  updateCategory(
    categoryId: string,
    payload: ProductCategoryMutation,
  ): Observable<ProductCategory> {
    return this.api
      .put<
        | ApiResponse<ProductCategory | { category: ProductCategory }>
        | ProductCategory
        | { category: ProductCategory },
        FormData
      >(
        `${API_ENDPOINTS.products.adminCategories}/${categoryId}`,
        this.catalogFormData(payload),
        'marketplace',
        environment.catalogUploadTimeoutMs,
      )
      .pipe(map((response) => this.extractCatalogItem(unwrapApiResponse(response), 'category')));
  }

  deactivateCategory(categoryId: string): Observable<void> {
    return this.api
      .delete<ApiResponse<unknown> | unknown>(
        `${API_ENDPOINTS.products.adminCategories}/${categoryId}`,
        'marketplace',
      )
      .pipe(map(() => undefined));
  }

  createBrand(payload: ProductBrandMutation): Observable<ProductBrand> {
    return this.api
      .post<
        | ApiResponse<ProductBrand | { brand: ProductBrand }>
        | ProductBrand
        | { brand: ProductBrand },
        FormData
      >(
        API_ENDPOINTS.products.adminBrands,
        this.catalogFormData(payload),
        'marketplace',
        environment.catalogUploadTimeoutMs,
      )
      .pipe(map((response) => this.extractCatalogItem(unwrapApiResponse(response), 'brand')));
  }

  updateBrand(brandId: string, payload: ProductBrandMutation): Observable<ProductBrand> {
    return this.api
      .put<
        | ApiResponse<ProductBrand | { brand: ProductBrand }>
        | ProductBrand
        | { brand: ProductBrand },
        FormData
      >(
        `${API_ENDPOINTS.products.adminBrands}/${brandId}`,
        this.catalogFormData(payload),
        'marketplace',
        environment.catalogUploadTimeoutMs,
      )
      .pipe(map((response) => this.extractCatalogItem(unwrapApiResponse(response), 'brand')));
  }

  deactivateBrand(brandId: string): Observable<void> {
    return this.api
      .delete<ApiResponse<unknown> | unknown>(
        `${API_ENDPOINTS.products.adminBrands}/${brandId}`,
        'marketplace',
      )
      .pipe(map(() => undefined));
  }

  profitRules(categoryId: string): Observable<CategoryProfitRule[]> {
    return this.api
      .get<
        | ApiResponse<{ rules: CategoryProfitRule[] } | CategoryProfitRule[]>
        | { rules: CategoryProfitRule[] }
        | CategoryProfitRule[]
      >(
        API_ENDPOINTS.products.categoryProfitRules(categoryId),
        undefined,
        'marketplace',
        environment.catalogUploadTimeoutMs,
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => (Array.isArray(response) ? response : (response.rules ?? []))),
      );
  }

  createProfitRule(
    categoryId: string,
    payload: CategoryProfitRuleMutation,
  ): Observable<CategoryProfitRule> {
    return this.api
      .post<ApiResponse<CategoryProfitRule> | CategoryProfitRule, CategoryProfitRuleMutation>(
        API_ENDPOINTS.products.categoryProfitRules(categoryId),
        payload,
        'marketplace',
        environment.catalogUploadTimeoutMs,
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  updateProfitRule(
    ruleId: string,
    payload: CategoryProfitRuleMutation,
  ): Observable<CategoryProfitRule> {
    return this.api
      .put<ApiResponse<CategoryProfitRule> | CategoryProfitRule, CategoryProfitRuleMutation>(
        API_ENDPOINTS.products.profitRule(ruleId),
        payload,
        'marketplace',
        environment.catalogUploadTimeoutMs,
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  deactivateProfitRule(ruleId: string): Observable<void> {
    return this.api
      .delete<ApiResponse<unknown> | unknown>(
        API_ENDPOINTS.products.profitRule(ruleId),
        'marketplace',
      )
      .pipe(map(() => undefined));
  }

  private toFormData(payload: CreateProductRequest): FormData {
    const formData = new FormData();
    const itemType = payload.itemType ?? payload.subCategory;

    this.append(formData, 'sellerUserId', payload.sellerUserId);
    this.append(formData, 'sellerTenantId', payload.sellerTenantId);
    this.append(formData, 'categoryId', payload.categoryId);
    this.append(formData, 'brandId', payload.brandId);
    this.append(formData, 'title', payload.title);
    this.append(formData, 'description', payload.description);
    this.append(formData, 'modelName', payload.modelName);
    this.append(formData, 'mainCategory', payload.mainCategory);
    this.append(formData, 'itemType', itemType);
    this.append(formData, 'condition', payload.listingType);
    this.append(formData, 'cost', payload.cost);
    this.append(formData, 'repairCost', payload.repairCost);
    this.append(formData, 'extraCost', payload.extraCost);
    this.append(formData, 'isReadyToSell', String(payload.isReadyToSell));
    this.append(formData, 'currency', payload.currency);
    this.append(formData, 'addressLine1', payload.addressLine1);
    this.append(formData, 'city', payload.city);
    this.append(formData, 'area', payload.area);
    this.append(formData, 'state', payload.state);
    this.append(formData, 'country', payload.country);
    this.append(formData, 'zipCode', payload.zipCode);
    this.append(formData, 'Latitude', payload.latitude);
    this.append(formData, 'Longitude', payload.longitude);
    this.append(formData, 'storeId', payload.storeId);
    this.append(
      formData,
      'existingSellerVerificationImageUrl',
      payload.existingSellerVerificationImageUrl,
    );
    this.append(formData, 'existingOriginalBillUrl', payload.existingOriginalBillUrl);
    this.append(formData, 'existingImageUrlsJson', payload.existingImageUrls?.length ? JSON.stringify(payload.existingImageUrls) : undefined);

    for (const file of payload.imageFiles ?? []) {
      formData.append('images', file, file.name);
    }
    if (payload.sellerVerificationImage) {
      formData.append(
        'sellerVerificationImage',
        payload.sellerVerificationImage,
        payload.sellerVerificationImage.name,
      );
    }
    if (payload.originalBill) {
      formData.append('originalBill', payload.originalBill, payload.originalBill.name);
    }

    return formData;
  }

  private catalogFormData(payload: ProductCategoryMutation | ProductBrandMutation): FormData {
    const formData = new FormData();
    if ('parentCategoryId' in payload) {
      this.append(formData, 'parentCategoryId', payload.parentCategoryId);
      this.append(formData, 'itemType', payload.itemType);
    }
    if ('categoryId' in payload) {
      this.append(formData, 'categoryId', payload.categoryId);
    }
    this.append(formData, 'name', payload.name);
    this.append(formData, 'slug', payload.slug);
    this.append(formData, 'mainCategory', payload.mainCategory);
    this.append(formData, 'isActive', String(payload.isActive));
    if (payload.icon) {
      formData.append('Icon', payload.icon, payload.icon.name);
    }
    return formData;
  }

  private extractCatalogItem<T>(response: T | Record<string, T>, key: string): T {
    return typeof response === 'object' && response !== null && key in response
      ? (response as Record<string, T>)[key]
      : (response as T);
  }

  private append(formData: FormData, key: string, value: string | number | undefined): void {
    if (value !== undefined && value !== '') {
      formData.append(key, String(value));
    }
  }

  private extractCollection<T>(value: unknown, key: string): T[] {
    if (Array.isArray(value)) {
      return value as T[];
    }

    if (typeof value !== 'object' || value === null) {
      return [];
    }

    const nested = (value as Record<string, unknown>)[key];
    return Array.isArray(nested) ? (nested as T[]) : [];
  }

  private extractProducts(response: Product[] | PagedResponse<Product>): Product[] {
    if (Array.isArray(response)) {
      return response;
    }

    const productResponse = response as PagedResponse<Product> & { products?: Product[] };
    return productResponse.products ?? response.items ?? response.data ?? response.results ?? [];
  }
}
